/**
 * Catálogo central de módulos licenciables (mirror de backend/catalog_modulos.py).
 * Agregar un módulo = registrar aquí + backend/catalog_modulos.py.
 */
import { WORK_MODULES, HUB_TRAMITES } from "@/modules/workModuleCore";

export const DEFAULT_MODULE_CATALOG = [
  {
    id: "visor",
    label: "Visor SAT / CFDI",
    description: "Carga, visor XML, confronta, descarga SAT y auditoría EFOS.",
    default_entitled: true,
    work_module_id: WORK_MODULES.VISOR_SAT,
    licensable: true,
    enabled: true,
    status: "active",
    visible_en_trial: true,
    visible_en_solicitud: true,
    visible_en_centro_saas: true,
    requiere_autorizacion: true,
    checkbox_order: 1,
  },
  {
    id: "laboral",
    label: "Asistente Laboral / REPSE",
    description: "Registros patronales, catálogo, validación NSS/IMSS y control REPSE.",
    default_entitled: false,
    work_module_id: WORK_MODULES.ASISTENTE_LABORAL,
    licensable: true,
    enabled: true,
    status: "active",
    visible_en_trial: true,
    visible_en_solicitud: true,
    visible_en_centro_saas: true,
    requiere_autorizacion: true,
    checkbox_order: 2,
  },
  {
    id: "tramites",
    label: "Trámites Fiscales",
    description: "Validación RFC, inscripciones SAT/IMSS y NSS (IDSE).",
    default_entitled: true,
    work_module_id: HUB_TRAMITES,
    licensable: true,
    enabled: true,
    status: "active",
    visible_en_trial: true,
    visible_en_solicitud: true,
    visible_en_centro_saas: true,
    requiere_autorizacion: true,
    checkbox_order: 3,
  },
  {
    id: "despacho",
    label: "Gestión Despacho",
    description:
      "Control interno del despacho, usuarios internos, clientes, tareas, permisos y operación diaria.",
    default_entitled: false,
    work_module_id: WORK_MODULES.GESTION_DESPACHO,
    licensable: true,
    enabled: true,
    status: "active",
    visible_en_trial: true,
    visible_en_solicitud: true,
    visible_en_centro_saas: true,
    requiere_autorizacion: true,
    checkbox_order: 4,
  },
];

/** Módulos activos en producto — emisión Centro BNT (mirror backend catalog_module_ids). */
export const PRODUCT_LICENSABLE_MODULE_IDS = ["visor", "laboral", "tramites", "despacho"];

/** Catálogo ordenado para checkboxes de licencia de organización. */
export function getProductLicensableModules(catalog = DEFAULT_MODULE_CATALOG) {
  const order = Object.fromEntries(PRODUCT_LICENSABLE_MODULE_IDS.map((id, idx) => [id, idx]));
  return catalog
    .filter((m) => PRODUCT_LICENSABLE_MODULE_IDS.includes(m.id))
    .sort((a, b) => order[a.id] - order[b.id]);
}

/** Flags por defecto al emitir licencia (visor/tramites true, laboral/despacho false). */
export function defaultProductModuleFlags(catalog = DEFAULT_MODULE_CATALOG) {
  return Object.fromEntries(
    getProductLicensableModules(catalog).map((m) => [m.id, Boolean(m.default_entitled)]),
  );
}

const WORK_MODULE_TO_ENTITLEMENT = Object.fromEntries(
  DEFAULT_MODULE_CATALOG.filter((m) => m.work_module_id).map((m) => [m.work_module_id, m.id]),
);

export function resolveModuleCatalog(systemInfo) {
  const fromApi = systemInfo?.module_catalog;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi;
  }
  return DEFAULT_MODULE_CATALOG;
}

export function defaultEntitlements(catalog = DEFAULT_MODULE_CATALOG) {
  return Object.fromEntries(
    catalog.map((m) => [m.id, Boolean(m.default_entitled)]),
  );
}

export function entitlementIdForWorkModule(workModuleId) {
  if (workModuleId === WORK_MODULES.MATERIALIDAD) {
    // Misma licencia comercial que Trámites Fiscales (sin SKU aparte aún).
    return "tramites";
  }
  return WORK_MODULE_TO_ENTITLEMENT[workModuleId] || null;
}

/**
 * Resuelve si un entitlement está activo (v2 entitlements o legacy modulo_*).
 */
export function isEntitlementActive(entitlementId, { entitlements, modulePermissions } = {}) {
  if (entitlements && typeof entitlements === "object" && entitlementId in entitlements) {
    return Boolean(entitlements[entitlementId]);
  }
  const perms = modulePermissions || {};
  if (entitlementId === "visor") return Boolean(perms.modulo_visor_sat ?? true);
  if (entitlementId === "laboral") return Boolean(perms.modulo_asistente_laboral);
  if (entitlementId === "tramites") {
    return Boolean(perms.modulo_tramites_fiscales ?? perms.modulo_visor_sat ?? true);
  }
  if (entitlementId === "despacho") {
    return Boolean(perms.modulo_gestion_despacho);
  }
  return false;
}

export function isWorkModuleEntitled(workModuleId, ctx) {
  const eid = entitlementIdForWorkModule(workModuleId);
  if (!eid) return true;
  return isEntitlementActive(eid, ctx);
}
